import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { SwitchField } from '@/components/ui/switch';
import { useMemberPicker } from '@/pages/pastoral/use-member-picker';

import { SacramentImagesFields } from './sacrament-images';
import type { SacramentPersonDetails } from './sacrament-people-fields';

export const SacramentPersonDetailsDialog = ({
  open,
  personName,
  value,
  allowCreateMember = false,
  memberLinked = false,
  onOpenChange,
  onSave,
}: {
  open: boolean;
  personName: string;
  value: SacramentPersonDetails;
  allowCreateMember?: boolean;
  memberLinked?: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (value: SacramentPersonDetails) => void;
}) => {
  const { t } = useTranslation('sacraments');
  const { t: tCommon } = useTranslation();
  const [draft, setDraft] = useState(value);
  const motherPicker = useMemberPicker(open, 'sacrament-mother');
  const fatherPicker = useMemberPicker(open, 'sacrament-father');

  const displayName = personName.trim() || t('people.extraDetailsUnnamed');

  const setParent = (
    side: 'mother' | 'father',
    memberId: string | null,
    memberLabel: string | undefined,
  ) => {
    if (side === 'mother') {
      setDraft((current) => ({
        ...current,
        motherMemberId: memberId,
        motherName: memberLabel ?? current.motherName,
      }));
      return;
    }
    setDraft((current) => ({
      ...current,
      fatherMemberId: memberId,
      fatherName: memberLabel ?? current.fatherName,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{t('people.extraDetailsTitle')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-6">
          <div className="space-y-4">
            <div>
              <p className="text-body font-medium text-fg">{displayName}</p>
              <p className="text-caption text-fg-secondary">{t('people.photoHint')}</p>
            </div>
            {allowCreateMember && !memberLinked ? (
              <SwitchField
                id="sacrament-person-create-member"
                label={t('people.createMember.label')}
                description={t('people.createMember.description')}
                checked={draft.createMember}
                onCheckedChange={(checked) =>
                  setDraft((current) => ({ ...current, createMember: checked }))
                }
              />
            ) : null}
          </div>
          <SacramentImagesFields
            personName={displayName}
            value={draft}
            onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
          />

          <div className="grid gap-6 sm:grid-cols-2">
            <ParentFields
              label={t('people.mother')}
              memberId={draft.motherMemberId}
              name={draft.motherName}
              options={motherPicker.options}
              loading={motherPicker.loading}
              onQueryChange={motherPicker.setQuery}
              clearLabel={tCommon('action.clearSelection')}
              onMemberChange={(id, label) => setParent('mother', id, label)}
              onNameChange={(name) => setDraft((current) => ({ ...current, motherName: name }))}
            />
            <ParentFields
              label={t('people.father')}
              memberId={draft.fatherMemberId}
              name={draft.fatherName}
              options={fatherPicker.options}
              loading={fatherPicker.loading}
              onQueryChange={fatherPicker.setQuery}
              clearLabel={tCommon('action.clearSelection')}
              onMemberChange={(id, label) => setParent('father', id, label)}
              onNameChange={(name) => setDraft((current) => ({ ...current, fatherName: name }))}
            />
          </div>
        </DialogBody>
        <DialogFooter className="justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {tCommon('action.cancel')}
          </Button>
          <Button type="button" onClick={() => onSave(draft)}>
            {t('people.saveDetails')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ParentFields = ({
  label,
  memberId,
  name,
  options,
  loading,
  onQueryChange,
  clearLabel,
  onMemberChange,
  onNameChange,
}: {
  label: string;
  memberId: string | null;
  name: string;
  options: { id: string; label: string }[];
  loading: boolean;
  onQueryChange: (query: string) => void;
  clearLabel: string;
  onMemberChange: (id: string | null, label: string | undefined) => void;
  onNameChange: (name: string) => void;
}) => {
  const { t } = useTranslation('sacraments');

  return (
    <div className="flex flex-col gap-4">
      <p className="text-body-sm font-medium text-fg">{label}</p>
      <FormField label={t('people.parentMember')}>
        {(field) => (
          <EntityCombobox
            id={field.id}
            value={memberId}
            onChange={(id) => {
              const memberLabel = id ? options.find((option) => option.id === id)?.label : undefined;
              onMemberChange(id, memberLabel ?? (id ? name : undefined));
            }}
            options={options}
            selected={
              memberId
                ? options.find((option) => option.id === memberId) ?? {
                    id: memberId,
                    label: name || memberId,
                  }
                : null
            }
            onQueryChange={onQueryChange}
            loading={loading}
            placeholder={t('memberPicker.placeholder')}
            emptyMessage={t('memberPicker.empty')}
            clearLabel={clearLabel}
            popoverPortal="dialog"
          />
        )}
      </FormField>
      <FormField label={t('people.parentName')}>
        {(field) => (
          <Input
            id={field.id}
            value={name}
            readOnly={Boolean(memberId)}
            placeholder={memberId ? undefined : t('people.parentNamePlaceholder')}
            onChange={(event) => onNameChange(event.target.value)}
          />
        )}
      </FormField>
    </div>
  );
};
